/**
 * HART cinematic sections — the dramatic bits of /solutions/ims-hart.
 * Deck source: HART Executive Deck 10.10.2026 (TechInquest). All dollar figures
 * are illustrative and labelled as such on the page.
 */
import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  TrendingUp, AlertTriangle, Clock, User, CircleDollarSign, Activity, Play, Pause,
} from "lucide-react";

const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

/* ---------------------------------------------------------------- counters */
export const Counter = ({ to, prefix = "", suffix = "", decimals = 0, duration = 1.6, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduce = useReducedMotion();
  const [v, setV] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) return setV(to);
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / (duration * 1000), 1);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduce]);

  return (
    <span ref={ref} className={`font-mono tabular-nums ${className}`}>
      {prefix}{v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}
    </span>
  );
};

/* ------------------------------------------------------- data stream field */
export const DataStream = ({ className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      {Array.from({ length: 14 }).map((_, i) => (
        <motion.span key={i}
          className="absolute top-0 w-px bg-gradient-to-b from-transparent via-sky-400/50 to-transparent"
          style={{ left: `${(i * 7.3 + 3) % 100}%`, height: `${18 + (i % 4) * 9}%` }}
          animate={{ y: ["-25%", "520%"], opacity: [0, 0.9, 0] }}
          transition={{ duration: 4.2 + (i % 5) * 0.8, repeat: Infinity, delay: i * 0.45, ease: "linear" }}
        />
      ))}
    </div>
  );
};

/* ----------------------------------------------------- live tier simulator */
const TIER_TARGET = 5_000_000;
const BASE_SPEND = 4_160_000;      // illustrative: $840K short of Tier 3
const GAP = TIER_TARGET - BASE_SPEND;
const BONUS = 103_000;

export const TierSimulator = () => {
  const reduce = useReducedMotion();
  const [extra, setExtra] = useState(0);
  const [running, setRunning] = useState(false);
  const spend = BASE_SPEND + extra;
  const pct = Math.min((spend / TIER_TARGET) * 100, 100);
  const unlocked = spend >= TIER_TARGET;
  const remaining = Math.max(TIER_TARGET - spend, 0);

  useEffect(() => {
    if (!running) return;
    if (reduce) { setExtra(GAP); setRunning(false); return; }
    let raf; const t0 = performance.now(); const from = extra;
    const tick = (t) => {
      const p = Math.min((t - t0) / 2200, 1);
      setExtra(from + (GAP - from) * p);
      if (p < 1) raf = requestAnimationFrame(tick); else setRunning(false);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative rounded-lg border border-white/10 bg-[#0F172A] p-6 md:p-8 overflow-hidden shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)]"
      data-testid="tier-simulator">
      <DataStream className="opacity-[0.08]" />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-7">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-sky-400 mb-1.5">
              Live tier monitoring
            </p>
            <h3 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
              Supplier agreement · Tier 3 threshold
            </h3>
            <p className="text-sm text-slate-400 mt-1.5">
              Position recalculates on every purchase. Period still open.
            </p>
          </div>
          <button onClick={() => setRunning((r) => !r)}
            className="inline-flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/10
              px-4 py-2 text-xs font-bold uppercase tracking-wider text-sky-300 transition-colors
              hover:bg-sky-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            data-testid="tier-simulator-run">
            {running ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {unlocked ? "Replay the quarter" : "Run the near-miss"}
          </button>
        </div>

        <div className="flex items-end justify-between gap-6 mb-2.5 font-mono">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Qualified spend</p>
            <p className="text-2xl md:text-3xl font-bold text-white tabular-nums"
              data-testid="tier-spend">{money(spend)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Tier 3 at</p>
            <p className="text-lg font-bold text-slate-400 tabular-nums">{money(TIER_TARGET)}</p>
          </div>
        </div>

        <div className="relative h-4 rounded-sm bg-white/[0.04] border border-white/10 overflow-hidden">
          <motion.div
            className={`absolute inset-y-0 left-0 ${unlocked
              ? "bg-emerald-500"
              : "bg-sky-500"}`}
            animate={{ width: `${pct}%` }}
            transition={{ type: "spring", stiffness: 60, damping: 18 }}
          />
          {!reduce && (
            <motion.div className="absolute inset-y-0 w-24 bg-white/20 blur-md"
              animate={{ x: ["-10%", "420%"] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: "linear" }} />
          )}
          <span className="absolute inset-y-0 right-0 w-px bg-white/40" />
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Gap to next tier</p>
            <p className={`text-xl font-mono font-bold tabular-nums ${remaining ? "text-white" : "text-emerald-400"}`}
              data-testid="tier-gap">{remaining ? money(remaining) : "Threshold met"}</p>
          </div>
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Upside on the table</p>
            <p className="text-xl font-mono font-bold text-sky-400 tabular-nums">{money(BONUS)}</p>
          </div>
          <div className={`rounded-md border p-4 transition-colors ${unlocked
            ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]"}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Status</p>
            <p className={`text-sm font-bold ${unlocked ? "text-emerald-400" : "text-slate-300"}`}
              data-testid="tier-status">
              {unlocked ? `Tier 3 unlocked · ${money(BONUS)} recovered` : "Near-miss — caught while open"}
            </p>
          </div>
        </div>

        <p className="mt-5 text-[11px] text-slate-500">
          Illustrative agreement and figures. Without live monitoring this quarter closes
          {" "}{money(GAP)} short and the {money(BONUS)} never arrives.
        </p>
      </div>
    </div>
  );
};

/* --------------------------------------------------------- alert workbench */
const ALERTS = [
  { type: "Opportunity near-miss", amount: 103000, amountLabel: "upside", sounds: "You're $840K from the next tier worth $103K more.",
    clock: "Before period close · 4 days", owner: "Sarah Jenkins · Sr. Sourcing Lead", tone: "cyan" },
  { type: "Rebate shortfall", amount: 184200, amountLabel: "under-remitted", sounds: "Q2 tier payment under-remitted.",
    clock: "Dispute window · 12 days", owner: "Marcus Vance · Rebate Analyst", tone: "rose" },
  { type: "Overdue payment", amount: 96500, amountLabel: "owed", sounds: "Earned rebate past due.",
    clock: "21 days past due", owner: "David Chen · Finance Operations", tone: "amber" },
  { type: "Calculation variance", amount: 42800, amountLabel: "per-line gap", sounds: "Invoice price differs from contract price.",
    clock: "Ongoing reconciliation", owner: "H.E.A.R.T matching engine", tone: "emerald" },
  { type: "Expiring contract", amount: 1250000, amountLabel: "value at risk", sounds: "Renewal window closes in 12 days.",
    clock: "12 days", owner: "Elena Rostova · VP Supply Chain", tone: "violet" },
];

const TONE = {
  cyan: { ring: "border-sky-300", text: "text-sky-700", dot: "bg-sky-600", glow: "shadow-cyan-500/10" },
  rose: { ring: "border-rose-300", text: "text-rose-700", dot: "bg-rose-500", glow: "shadow-rose-500/10" },
  amber: { ring: "border-amber-300", text: "text-amber-700", dot: "bg-amber-500", glow: "shadow-amber-500/10" },
  emerald: { ring: "border-emerald-300", text: "text-emerald-700", dot: "bg-emerald-500", glow: "shadow-emerald-500/10" },
  violet: { ring: "border-violet-400/35", text: "text-violet-700", dot: "bg-violet-500", glow: "shadow-violet-500/10" },
};

export const AlertWorkbench = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();
  const ranked = [...ALERTS].sort((a, b) => b.amount - a.amount);

  return (
    <div ref={ref} className="relative rounded-3xl border border-slate-200 bg-white p-5 md:p-7 shadow-[0_2px_4px_rgba(11,37,69,0.04),0_20px_50px_-28px_rgba(11,37,69,0.3)]"
      data-testid="alert-workbench">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-sky-700" />
          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-sky-700">
            Analyst workbench · ranked by money, not by date
          </p>
        </div>
        <p className="text-[11px] text-slate-500 font-mono">5 open · illustrative queue</p>
      </div>

      <div className="space-y-3">
        {ranked.map((a, i) => {
          const t = TONE[a.tone];
          return (
            <motion.div key={a.type}
              initial={reduce ? false : { opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.01 }}
              transition={{ delay: 0.1 * i, type: "spring", stiffness: 70, damping: 16 }}
              className={`group grid grid-cols-1 md:grid-cols-[1.5fr_auto_1fr] gap-4 items-center overflow-hidden rounded-2xl border
                ${t.ring} bg-slate-50 px-4 py-4 shadow-[0_1px_2px_rgba(11,37,69,0.06),0_8px_24px_-12px_rgba(11,37,69,0.18)] transition-colors hover:bg-slate-100`}
              data-testid={`alert-card-${i}`}>
              <div className="min-w-0 break-words">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                  <p className={`text-[10px] font-black uppercase tracking-[0.18em] ${t.text}`}>{a.type}</p>
                </div>
                <p className="text-sm md:text-[15px] font-semibold text-[#0b2545] leading-snug">“{a.sounds}”</p>
              </div>
              <div className="md:text-right min-w-0">
                <p className={`font-mono text-xl md:text-2xl font-bold tabular-nums ${t.text}`}>
                  {money(a.amount)}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{a.amountLabel}</p>
              </div>
              <div className="space-y-1 min-w-0">
                <p className="flex items-center gap-1.5 text-[12px] text-slate-600 font-mono">
                  <Clock className="w-3.5 h-3.5 text-slate-500" /> {a.clock}
                </p>
                <p className="flex items-center gap-1.5 text-[12px] text-slate-600">
                  <User className="w-3.5 h-3.5 text-slate-500" /> {a.owner}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-700 mb-1.5">The rule</p>
        <p className="text-sm text-amber-900/90 leading-relaxed">
          Every alert lands with <strong>dollars at stake</strong>, <strong>days remaining</strong> and
          an <strong>owner</strong>. An alert without all three is a notification — and a notification
          is not a recovery.
        </p>
      </div>
    </div>
  );
};

/* ---------------------------------------------------- follow one dollar */
const STEPS = [
  { k: "Set up", who: "Finance · one-time", d: "Agreements, tier tables and the rebate budget loaded — self-service." },
  { k: "Feed", who: "ERP · continuous", d: "Purchases, invoices and remittances stream from the ERP you already own." },
  { k: "Match", who: "H.E.A.R.T engine · real time", d: "Every line tied to its contract, tier and measurement window." },
  { k: "Track & alert", who: "H.E.A.R.T engine · on every PO", d: "Tiers recalculate on every purchase; five alert types fire in time to act." },
  { k: "Work", who: "Named analyst · until closed", d: "Alerts become assigned tasks with escalation paths and a full audit trail." },
  { k: "Recover", who: "Supplier portal", d: "Disputes resolved and dollars assigned to the right department." },
];

export const FollowOneDollar = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <div ref={ref} className="relative" data-testid="follow-one-dollar">
      <div className="absolute left-[22px] md:left-1/2 top-2 bottom-2 w-px bg-white/[0.08] md:-translate-x-1/2" />
      <motion.div
        className="absolute left-[22px] md:left-1/2 top-2 w-px bg-gradient-to-b from-sky-500 via-sky-400/60 to-transparent md:-translate-x-1/2"
        initial={reduce ? false : { height: 0 }}
        whileInView={{ height: "97%" }}
        viewport={{ once: true, amount: 0.01 }}
        transition={{ duration: 2.4, ease: "easeInOut" }}
      />
      <div className="space-y-5">
        {STEPS.map((s, i) => (
          <motion.div key={s.k}
            initial={reduce ? false : { opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.01 }}
            transition={{ delay: 0.16 * i, duration: 0.5 }}
            className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-10 ${i % 2 ? "md:text-left" : "md:text-right"}`}
            data-testid={`dollar-step-${i}`}>
            <div className={`${i % 2 ? "md:col-start-2" : "md:col-start-1"}`}>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-sky-300 transition-colors">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-sky-700 mb-1">
                  Step {i + 1} · {s.who}
                </p>
                <h4 className="text-lg font-extrabold text-[#0b2545] tracking-tight mb-1">{s.k}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            </div>
            <span className="absolute left-[14px] md:left-1/2 top-7 md:-translate-x-1/2 w-[18px] h-[18px]
              rounded-full border-2 border-sky-600 bg-[#f7f9fc] flex items-center justify-center">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-600" />
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* --------------------------------------------------------- leak pillars */
export const LeakPillars = () => {
  const items = [
    { t: "The contract lives in a PDF", d: "The tiers live in an inbox. Nobody sees the next tier until the period has closed." },
    { t: "Remittances are never re-checked", d: "Under-payments against contract math are accepted silently." },
    { t: "Deadlines sit in one calendar", d: "Renewal windows close unseen while the analyst fights other fires." },
    { t: "Recovery is nobody's job title", d: "Found money without an owner is not found money." },
  ];
  const reduce = useReducedMotion();
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="leak-pillars">
      {items.map((it, i) => (
        <motion.div key={it.t}
          initial={reduce ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          transition={{ delay: 0.08 * i, duration: 0.45 }}
          className="group relative rounded-lg border border-slate-200 bg-white p-6 overflow-hidden
            hover:border-[#0284C7]/50 hover:shadow-lg hover:shadow-slate-300/40 transition-colors duration-200">
          <span className="absolute inset-y-0 left-0 w-px bg-slate-200 group-hover:bg-[#0284C7] transition-colors duration-200" />
          <p className="text-[11px] font-mono tabular-nums text-slate-400 mb-3 tracking-[0.18em]">
            0{i + 1}
          </p>
          <h4 className="text-[15px] font-bold text-[#0a1c3c] mb-1.5">{it.t}</h4>
          <p className="text-sm text-slate-600 leading-relaxed">{it.d}</p>
        </motion.div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------- near-miss moment */
export const NearMiss = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.97 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.05 }}
    transition={{ duration: 0.6 }}
    className="relative overflow-hidden rounded-lg border border-slate-200 bg-white p-7 md:p-11 shadow-sm"
    data-testid="near-miss-block">
    <DataStream className="opacity-[0.14]" />
    <div className="relative">
      <p className="text-[10px] font-black uppercase tracking-[0.26em] text-[#0284C7] mb-4">
        The near-miss — the most expensive failure
      </p>
      <h3 className="font-display text-2xl md:text-4xl font-bold text-[#0a1c3c] tracking-tight leading-[1.12] mb-8">
        The costliest rebate failure isn&apos;t a missed payment.
        <span className="text-slate-400"> It&apos;s the tier you nearly hit.</span>
      </h3>
      <div className="flex flex-wrap items-end gap-10 md:gap-14 mb-7 border-t border-slate-200 pt-7">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Purchasing short of the tier</p>
          <Counter to={840} prefix="$" suffix="K" className="text-4xl md:text-5xl font-bold text-[#0a1c3c] tabular-nums tracking-tight" />
        </div>
        <TrendingUp className="w-6 h-6 text-slate-400 mb-3" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Discount left behind</p>
          <Counter to={103} prefix="$" suffix="K" className="text-4xl md:text-5xl font-bold text-[#0284C7] tabular-nums tracking-tight" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Seen</p>
          <p className="text-lg md:text-xl font-mono text-slate-500">after the quarter closed</p>
        </div>
      </div>
      <p className="text-base md:text-lg text-slate-600 max-w-3xl leading-relaxed">
        This money isn&apos;t lost to negotiation. <strong className="text-[#0a1c3c] font-semibold">It&apos;s lost to the
        calendar.</strong> H.E.A.R.T recalculates your tier position on every purchase, so a near-miss
        surfaces while the period is still open — with the dollars, the days and the owner attached.
      </p>
      <p className="mt-5 text-[11px] text-slate-500 flex items-center gap-1.5">
        <CircleDollarSign className="w-3.5 h-3.5" />
        Illustrative figures drawn from typical health-system contract structures.
      </p>
    </div>
  </motion.div>
);
